
/**
 * Direct EmailJS implementation strategy
 */
import type { EmailFormData } from '../emailTemplates';
import type { EmailResult } from '../emailTypes';
import { 
  createAdminTemplateParams, 
  createCustomerTemplateParams, 
  createAlternateCustomerParams 
} from '../emailTemplates';
import { getEmailJsConfig } from '../config/emailJsConfig';
import { initializeEmailJs, sendEmailWithEmailJs } from '../helpers/emailJsSender';

/**
 * Sends email using EmailJS directly
 */
export const sendEmailViaEmailJS = async (formData: EmailFormData): Promise<EmailResult> => {
  console.log('Using fallback EmailJS implementation...');
  
  // Get EmailJS configuration
  const config = getEmailJsConfig();
  
  // Initialize EmailJS with the public key
  initializeEmailJs(config);
  
  const adminTemplateParams = createAdminTemplateParams(formData);
  const customerTemplateParams = createCustomerTemplateParams(formData);
  
  console.log('Admin template params:', JSON.stringify(adminTemplateParams));
  console.log('Customer template params:', JSON.stringify(customerTemplateParams));
  
  // Try to send the customer email first (priority)
  let customerResult = null;
  let customerError = null;
  
  try {
    customerResult = await sendEmailWithEmailJs(
      config.serviceId, 
      config.customerTemplateId, 
      customerTemplateParams
    );
    console.log('Customer email sent successfully:', customerResult);
  } catch (error) {
    customerError = error;
    console.error('Failed to send customer email:', error);
    
    // If the primary attempt fails, try with slightly different parameters
    try {
      const altCustomerParams = createAlternateCustomerParams(customerTemplateParams);
      console.log("Attempting fallback with alternate parameters:", JSON.stringify(altCustomerParams));
      customerResult = await sendEmailWithEmailJs(
        config.serviceId, 
        config.customerTemplateId, 
        altCustomerParams
      );
      console.log('Customer email sent successfully with alternate parameters:', customerResult);
    } catch (altError) {
      console.error('Both customer email attempts failed:', altError);
    }
  }
  
  // Then try to send the admin email
  let adminResult = null;
  let adminError = null;
  
  try {
    adminResult = await sendEmailWithEmailJs(
      config.serviceId, 
      config.adminTemplateId, 
      adminTemplateParams
    );
    console.log('Admin email sent successfully:', adminResult);
  } catch (error) {
    adminError = error;
    console.error('Failed to send admin email:', error);
  }
  
  // Return result based on which emails were sent
  if (!adminResult && !customerResult) {
    throw new Error('Failed to send all emails');
  }
  
  return {
    success: true,
    adminSent: !!adminResult,
    customerSent: !!customerResult
  };
};
